import { useQuery } from '@apollo/client';
import { Card, CardContent, CardMedia, CircularProgress, Typography } from '@mui/material';
import type React from 'react';
import MEDIA_UNAVAILBE from 'src/assets/mediaUnavailble.jpeg';
import PDF_IMAGE from 'src/assets/pdfImage.png';
import useCollectionItemCardStyles from 'src/CollectionsPage/CollectionItems/CollectionItemView/CollectionItemCard/CollectionItemCardStyles';
import {
  FD_FILE_NAME_SLUG,
  FD_MIMETYPE_SLUG,
  FD_NAME_SLUG,
  MEDIA_PATH,
  PROXY_FORMAT_SLUG,
  SHARED_MEDIA_PATH,
  THUMBNAIL_FORMAT_SLUG,
} from 'src/constants';
import { buildFormatsQ, buildSharedFormatsQ } from 'src/dynamoQueries/queryHelpers';
import { getFirstNestedWithSaneDefault, getMetadataValue } from 'src/helpers/common';
import { genMediaUrl } from 'src/helpers/generateCustomFields';
import { useMetaQ } from 'src/hooks/meta';

interface CollectionListItemProps {
  id: string;
  shared: boolean;
}

interface FormatData {
  id?: string;
}

interface FormatsQueryResult {
  formats?: FormatData[];
  sharedFormats?: FormatData[];
  [key: string]: FormatData[] | undefined;
}

const CollectionListItem: React.FC<CollectionListItemProps> = ({ id, shared }) => {
  const classes = useCollectionItemCardStyles();

  const {
    data: thumbnailData,
    loading: loadingThumb,
    error: errorThumb,
  } = useQuery<FormatsQueryResult>(shared ? buildSharedFormatsQ() : buildFormatsQ(), {
    skip: !id,
    variables: { asset_Id: id, fmtType_Slug: THUMBNAIL_FORMAT_SLUG },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const thumbnailFormat = getFirstNestedWithSaneDefault<FormatData[]>(
    thumbnailData as Record<string, FormatData[]> | undefined,
    [{}],
  )[0];

  const {
    data: metadata,
    loading: loadingMeta,
    error: metaError,
  } = useMetaQ(
    {
      skip: !id,
      variables: { globalId: id, definitionSlugIn: [FD_NAME_SLUG] },
    },
    shared,
  );
  const {
    data: metadata2,
    loading: loadingMeta2,
    error: metaError2,
  } = useMetaQ(
    {
      skip: !id,
      variables: { globalId: id, definitionSlugIn: [FD_FILE_NAME_SLUG] },
    },
    shared,
  );

  const {
    data: proxyFormatData,
    loading: loadingProxy,
    error: errorProxy,
  } = useQuery<FormatsQueryResult>(shared ? buildSharedFormatsQ() : buildFormatsQ(), {
    skip: !id,
    variables: { asset_Id: id, fmtType_Slug: PROXY_FORMAT_SLUG },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const proxyFormat = getFirstNestedWithSaneDefault<FormatData[]>(
    proxyFormatData as Record<string, FormatData[]> | undefined,
    [{}],
  )[0];

  const {
    data: formatData,
    loading: loadingFormatData,
    error: formatDataError,
  } = useMetaQ(
    {
      skip: !proxyFormat?.id,
      variables: { globalId: proxyFormat?.id, definitionSlugIn: [FD_MIMETYPE_SLUG] },
    },
    shared,
  );

  const thumbMediaUrl = thumbnailFormat?.id
    ? genMediaUrl(shared ? SHARED_MEDIA_PATH : MEDIA_PATH, thumbnailFormat?.id)
    : MEDIA_UNAVAILBE;

  if (loadingMeta || loadingMeta2 || loadingThumb || loadingProxy || loadingFormatData)
    return <CircularProgress color="secondary" />;
  if (metaError || metaError2 || errorThumb || errorProxy || formatDataError)
    return <>{`Error! ${metaError?.message || errorThumb?.message}`}</>;

  const mimetype = getMetadataValue(formatData as Record<string, unknown> | undefined, FD_MIMETYPE_SLUG);
  const itemThumbnail = mimetype?.includes('pdf') ? PDF_IMAGE : thumbMediaUrl;

  const name = getMetadataValue(metadata as Record<string, unknown> | undefined, FD_NAME_SLUG);
  const filename = getMetadataValue(metadata2 as Record<string, unknown> | undefined, FD_FILE_NAME_SLUG);
  // This is to fall back to filename in the case name is undefined
  const shownName = name === undefined || name === null || name === '' ? filename : name;

  return (
    <Card classes={{ root: classes.card }} className={classes.root}>
      <CardMedia
        className={`${classes.cover} customRightClick`}
        component="img"
        image={itemThumbnail}
        title={shownName}
      />
      <div className={classes.details}>
        <CardContent className={classes.content}>
          <Typography component="h5" variant="h5">
            {shownName}
          </Typography>
        </CardContent>
      </div>
    </Card>
  );
};

export type { CollectionListItemProps };
export default CollectionListItem;
